import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Cart } from '@/modules/cart/domain/Cart';
import {
  addCartDiscount,
  addClubParis,
  addInvoice,
  addToSaveForLater,
  getCart,
  removeCartDiscount,
  removeCartOutOfStockItem,
  removeCartOutOfStockItems,
  removeClubParis,
  removeInvoice,
  removeLineItem,
  saveBillingAddress,
  saveShippingAddress,
  saveShippingAddressCommune,
  saveShippingAddressForGuest,
  selectShippingMethod,
  setOnBehalf,
  updateLineItemQuantity,
  updateShippingOption
} from '@/modules/cart/infrastructure/CartReduxRepository/CartReduxRepository';
import {
  addGiftCard,
  removeGiftCard
} from '@/modules/cart/infrastructure/CartReduxRepository/GiftCardThunks';
import { CART_INITIAL_STATE } from '@/modules/cart/infrastructure/CartReduxRepository/slices/CartSlice/constants';
import { extractOnBehalfDetails } from '@/modules/cart/infrastructure/CartReduxRepository/slices/CartSlice/utils';

const CartSlice = createSlice({
  name: 'cart',
  initialState: CART_INITIAL_STATE,
  reducers: {
    updateCart: (state, { payload }: PayloadAction<Partial<Cart>>) => {
      state.data = {
        ...state.data,
        ...(payload as Cart)
      };
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(getCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveShippingAddress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveShippingAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(saveShippingAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateLineItemQuantity.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateLineItemQuantity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(updateLineItemQuantity.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addCartDiscount.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addCartDiscount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(addCartDiscount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(removeCartDiscount.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeCartDiscount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeCartDiscount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(removeLineItem.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeLineItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(selectShippingMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(selectShippingMethod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(selectShippingMethod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateShippingOption.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateShippingOption.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(updateShippingOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(addGiftCard.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addGiftCard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(addGiftCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(removeGiftCard.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeGiftCard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeGiftCard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(addClubParis.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addClubParis.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addClubParis.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeClubParis.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeClubParis.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeClubParis.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addToSaveForLater.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addToSaveForLater.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(addToSaveForLater.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(removeCartOutOfStockItem.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeCartOutOfStockItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeCartOutOfStockItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(removeCartOutOfStockItems.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeCartOutOfStockItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeCartOutOfStockItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(setOnBehalf.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setOnBehalf.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        const onBehalfPayload = extractOnBehalfDetails(state.data);
        state.onBehalf = onBehalfPayload;
      })
      .addCase(setOnBehalf.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(addInvoice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addInvoice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(addInvoice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(removeInvoice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeInvoice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(removeInvoice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveShippingAddressForGuest.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveShippingAddressForGuest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(saveShippingAddressForGuest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveShippingAddressCommune.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveShippingAddressCommune.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(saveShippingAddressCommune.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(saveBillingAddress.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveBillingAddress.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(saveBillingAddress.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
});

export const { reducer: cartReducer, actions: CartActions } = CartSlice;
